import {
	MEAL_IDEAS_LOADED,
	MEAL_IDEAS_FAIL,
	MEAL_IDEAS_SAVE_BEGIN,
	MEAL_IDEA_SELECTED,
	MEAL_IDEA_GROUP_REORDERED,
	MEAL_IDEA_GROUP_MEMBERS_CHANGED,
	MEAL_IDEA_INGREDIENTS_REORDERED,
	MEAL_IDEA_SHOWN_AHEAD_SETTLED,
} from '../actions/mealIdeas';
import { CHANGE_ACTIVE_BUDGET_SUCCESS } from '../actions/sharing';
import { FOOD_LEGACY_RESET } from '../actions/food';

/**
 * The Meal Ideas screens' own state. The ideas and groups themselves are the food store's
 * (selectMealIdeasIndex in food/selectors.js); this keeps what is not the server's:
 *
 * - `loaded`: a meal_idea answer has come, which carries every idea and group, so there are ideas
 *   to show even while the food store has no copy of the budget (selectMealIdeasLoaded)
 * - `saving` / `error`: the last meal_idea write
 * - `selectedIdeaId`: the idea open in the Meal Ideas tab's popup
 * - `shownAhead`: changes on screen before their save has answered, which the selector lays over
 *   the food store's: `groups` holds a group's members by group id, `ingredients` an idea's
 *   ingredient ids in order by idea id. Each goes once its save has answered, either way: the food
 *   store then has the answer, or still has what was there before a refusal.
 */
const initialState = {
	loaded: false,
	saving: false,
	error: null,
	selectedIdeaId: null,
	shownAhead: { groups: {}, ingredients: {} },
};

/** Ids arrive as whatever the server sent; everything here compares them as numbers. */
export { normalizeIndex } from '../reducerUtilities/normalizeMealIdeaIndex';

const showAhead = (state, kind, id, ids) => ({
	...state,
	shownAhead: { ...state.shownAhead, [kind]: { ...state.shownAhead[kind], [id]: ids } },
});

export default function mealIdeasReducer(state = initialState, action) {
	switch(action.type) {
		// Another budget's ideas, or another user's; the food store loads the new budget's.
		case CHANGE_ACTIVE_BUDGET_SUCCESS:
		case FOOD_LEGACY_RESET: {
			return initialState;
		}
		case MEAL_IDEAS_SAVE_BEGIN: {
			return { ...state, saving: true, error: null };
		}
		case MEAL_IDEAS_FAIL: {
			return { ...state, saving: false, error: action.payload };
		}
		// The index itself goes to the food store (food/legacyChanges.js).
		case MEAL_IDEAS_LOADED: {
			const ideas = (action.payload && action.payload.ideas) || [];
			let selectedIdeaId = state.selectedIdeaId;
			if(selectedIdeaId !== null && !ideas.some((idea) => Number(idea.id) === selectedIdeaId)) {
				selectedIdeaId = null;
			}
			return {
				...state,
				selectedIdeaId,
				loaded: true,
				saving: false,
				error: null,
			};
		}
		case MEAL_IDEA_SELECTED: {
			return {
				...state,
				selectedIdeaId: action.payload === null ? null : Number(action.payload),
			};
		}
		// The thunks work out the new order from what is shown, and send it.
		case MEAL_IDEA_GROUP_REORDERED:
		case MEAL_IDEA_GROUP_MEMBERS_CHANGED: {
			const { groupId, mealIdeaIds } = action.payload;
			return showAhead(state, 'groups', Number(groupId), mealIdeaIds);
		}
		case MEAL_IDEA_INGREDIENTS_REORDERED: {
			const { mealIdeaId, ingredientIds } = action.payload;
			return showAhead(state, 'ingredients', Number(mealIdeaId), ingredientIds);
		}
		// Only when it is still the one that save showed: a later change shown since stays.
		case MEAL_IDEA_SHOWN_AHEAD_SETTLED: {
			const { kind, id, shown } = action.payload;
			if(!state.shownAhead[kind] || state.shownAhead[kind][id] !== shown) {
				return state;
			}
			const rest = { ...state.shownAhead[kind] };
			delete rest[id];
			return { ...state, shownAhead: { ...state.shownAhead, [kind]: rest } };
		}
		default:
			return state;
	}
}
